/**
 * Genie Purpose registry.
 *
 * What job is this Genie hired to do? Purpose is set at template pick
 * (each template declares its Purpose) and shown as a badge on the Genie
 * detail page. It also drives which Inbox tab a Genie's conversations
 * flow into.
 *
 * Single source of truth: edit this file when adding/renaming a Purpose
 * and the DB CHECK constraint in
 * `supabase/migrations/{ts}_add_purpose_to_agents.sql` in the same change.
 */
import type { LucideIcon } from "lucide-react";
import {
  CalendarCheck,
  ClipboardList,
  FileEdit,
  FileText,
  GraduationCap,
  LifeBuoy,
  Mic,
  ShoppingBag,
  Sparkles,
  Star,
  UserPlus,
} from "lucide-react";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";

export type GeniePurpose =
  | "leads"
  | "support"
  | "bookings"
  | "orders"
  | "quotes"
  | "surveys"
  | "forms"
  | "interviews"
  | "reviews"
  | "training"
  | "general";

export interface GeniePurposeMeta {
  /** Stable DB enum value. */
  id: GeniePurpose;
  /** Title-case label shown in badges and pickers. */
  label: string;
  /** One-line description of what a Genie with this Purpose does. */
  description: string;
  /** Lucide icon for small inline contexts (header badge, Inbox tabs). */
  icon: LucideIcon;
  genieIcon: GenieIconName;
  /**
   * `true` for Purposes whose Inbox tab + template support is not live
   * yet. They still appear in the picker and registry so we can ship
   * them progressively without code changes.
   */
  comingSoon?: boolean;
  /**
   * Inbox tab display order. `general` has no dedicated tab and is
   * omitted from the inbox surface.
   */
  inboxOrder: number | null;
}

export const GENIE_PURPOSES: GeniePurposeMeta[] = [
  {
    id: "leads",
    label: "Leads",
    description: "Capture and qualify new business inquiries.",
    icon: UserPlus,
    genieIcon: "target",
    inboxOrder: 1,
  },
  {
    id: "support",
    label: "Support",
    description: "Answer customer questions and resolve issues.",
    icon: LifeBuoy,
    genieIcon: "chat-check",
    inboxOrder: 2,
  },
  {
    id: "bookings",
    label: "Bookings",
    description: "Schedule appointments, calls, or service slots.",
    icon: CalendarCheck,
    genieIcon: "calendar-check",
    inboxOrder: 3,
    comingSoon: true,
  },
  {
    id: "orders",
    label: "Orders",
    description: "Take orders and route them to fulfilment.",
    icon: ShoppingBag,
    genieIcon: "files",
    inboxOrder: 4,
    comingSoon: true,
  },
  {
    id: "quotes",
    label: "Quotes",
    description: "Collect inputs and deliver estimates.",
    icon: FileText,
    genieIcon: "document",
    inboxOrder: 5,
    comingSoon: true,
  },
  {
    id: "surveys",
    label: "Surveys",
    description: "Run structured questionnaires and capture responses.",
    icon: ClipboardList,
    genieIcon: "clipboard-check",
    inboxOrder: 6,
    comingSoon: true,
  },
  {
    id: "forms",
    label: "Forms",
    description: "Collect structured submissions in a guided flow.",
    icon: FileEdit,
    genieIcon: "dashboard",
    inboxOrder: 7,
    comingSoon: true,
  },
  {
    id: "interviews",
    label: "Interviews",
    description: "Conduct structured conversations and capture answers.",
    icon: Mic,
    genieIcon: "microphone",
    inboxOrder: 8,
    comingSoon: true,
  },
  {
    id: "reviews",
    label: "Reviews",
    description: "Request and collect feedback after an interaction.",
    icon: Star,
    genieIcon: "smile",
    inboxOrder: 9,
    comingSoon: true,
  },
  {
    id: "training",
    label: "Training",
    description: "Coach customers or staff through learning material.",
    icon: GraduationCap,
    genieIcon: "brain",
    inboxOrder: 10,
    comingSoon: true,
  },
  {
    id: "general",
    label: "General",
    description: "A catch-all Genie without a dedicated job.",
    icon: Sparkles,
    genieIcon: "genie-lamp",
    inboxOrder: null,
  },
];

const PURPOSE_BY_ID: Record<GeniePurpose, GeniePurposeMeta> = GENIE_PURPOSES.reduce(
  (acc, meta) => {
    acc[meta.id] = meta;
    return acc;
  },
  {} as Record<GeniePurpose, GeniePurposeMeta>,
);

export const DEFAULT_GENIE_PURPOSE: GeniePurpose = "general";

export const getPurposeMeta = (purpose: GeniePurpose | string | null | undefined): GeniePurposeMeta => {
  if (purpose && (purpose as GeniePurpose) in PURPOSE_BY_ID) {
    return PURPOSE_BY_ID[purpose as GeniePurpose];
  }
  return PURPOSE_BY_ID[DEFAULT_GENIE_PURPOSE];
};

export const isValidGeniePurpose = (value: unknown): value is GeniePurpose =>
  typeof value === "string" && value in PURPOSE_BY_ID;
